import { FolderSimpleDashedIcon as ProjectPlaceholderIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  PROJECT_COLORS,
  PROJECT_ICONS,
  getProjectColor,
  getProjectIcon,
} from '@/utils/project-identity';

type Variant = 'standalone' | 'inline' | 'hero';

interface ProjectIconPickerProps {
  currentIcon?: string | null;
  currentColor?: string | null;
  onIconSelect: (iconId: string) => void;
  onColorSelect: (colorId: string) => void;
  onReset?: () => void;
  disabled?: boolean;
  /**
   * - `standalone`: legacy `size-12 rounded-xl` button.
   * - `inline`: left addon of an input-group. Borderless `h-full w-9` cell with
   *   right divider — fits inside the parent's bordered container.
   * - `hero`: detail-page hero. Renders as a plain project icon + a small
   *   dashed-bordered plus overlay top-right when no custom identity yet.
   *   NO wrapper card / NO bg tint — the icon glyph IS the identity.
   */
  variant?: Variant;
  className?: string;
}

/**
 * Identity = colored icon glyph. NEVER colored backgrounds.
 *
 * The previous treatment tinted both the icon AND the wrapper bg when a color
 * was picked — that read as childish (per user critique). Color now lives in
 * a single place: the icon glyph itself. The wrapper stays neutral or
 * disappears entirely (hero variant).
 *
 * Hero variant: just the project icon. When no custom identity, a tiny dashed
 * circle with a `+` glyph hovers in the top-right corner — that's the
 * "click to customize" affordance, classic and editorial. After picking, the
 * overlay collapses; the icon glyph adopts the chosen color.
 */
export function ProjectIconPicker({
  currentIcon,
  currentColor,
  onIconSelect,
  onColorSelect,
  onReset,
  disabled,
  variant = 'standalone',
  className,
}: ProjectIconPickerProps): JSX.Element {
  const [open, setOpen] = React.useState(false);

  // Empty state uses the dashed project glyph itself — the dashed shape IS
  // the "click to customize" affordance, no plus-overlay needed (and no
  // border-dashed wrapper). Filled state swaps to the user's chosen icon.
  const hasCustomIdentity = !!currentIcon || (!!currentColor && currentColor !== 'neutral');
  const Icon = hasCustomIdentity
    ? getProjectIcon(currentIcon ?? undefined)
    : ProjectPlaceholderIcon;
  const colorConfig = getProjectColor(currentColor ?? undefined);
  const selectedColorId = currentColor ?? 'neutral';

  const triggerClass = cn(
    {
      // Standalone — legacy size, kept for any caller still depending on it.
      // Tints both wrapper + icon (legacy behavior; new call sites should use
      // `inline` or `hero` instead).
      standalone: cn(
        'flex size-12 shrink-0 items-center justify-center rounded-xl',
        'transition-colors hover:opacity-80',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30',
        colorConfig.bgClass,
      ),
      // Inline — a quiet circular affordance inside the parent input. It does
      // not draw its own border, divider, or focus ring, so the project-name
      // field reads as one coherent control.
      inline: cn(
        'group flex size-9 shrink-0 items-center justify-center rounded-full p-0 md:size-7',
        'transition-colors hover:bg-muted/40 hover:text-foreground',
        'data-[state=open]:bg-muted/60 data-[state=open]:text-foreground',
        'focus-visible:outline-none focus-visible:ring-0',
      ),
      // Hero — plain icon target. No wrapper bg, no rounded card. The
      // adjacent `+` overlay (rendered separately below) handles affordance.
      hero: cn(
        'group relative inline-flex shrink-0 items-center justify-center',
        'transition-colors hover:opacity-70',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30 rounded-md',
        'p-1 -m-1', // hit area without changing visual size
      ),
    }[variant],
    className,
  );

  const iconSizeClass = {
    standalone: 'size-6',
    inline: 'size-4',
    hero: 'size-7',
  }[variant];

  // Color lives in the icon, never the wrapper.
  // Empty state hero gets `text-muted-foreground` (so the dashed-plus overlay
  // is the affordance); empty inline gets the same neutral.
  const iconColorClass = hasCustomIdentity ? colorConfig.textClass : 'text-muted-foreground';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="flat"
          size={variant === 'inline' ? 'icon-sm' : 'default'}
          className={triggerClass}
          aria-label="Pick icon and color"
        >
          {/* Single icon, no overlay. Empty state = ProjectPlaceholderIcon
              (dashed = "empty / customize"); filled state = user's chosen
              glyph in their chosen color. */}
          <Icon className={cn(iconSizeClass, iconColorClass)} weight="regular" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[calc(100vw-2rem)] max-w-[18.5rem] overflow-hidden p-0 md:w-64"
      >
        <div className="flex flex-col">
          <div
            className="grid grid-cols-6 gap-2 px-3 py-3"
            role="radiogroup"
            aria-label="Project color"
          >
            {PROJECT_COLORS.map((c) => (
              <Button
                key={c.id}
                type="button"
                variant="flat"
                size="icon-sm"
                className={cn(
                  'size-7 rounded-full p-0 transition-colors duration-150 md:size-6',
                  c.swatchClass,
                  selectedColorId === c.id
                    ? 'ring-2 ring-foreground/60 ring-offset-1 ring-offset-popover'
                    : 'hover:ring-1 hover:ring-foreground/30',
                )}
                onClick={() => onColorSelect(c.id)}
                aria-label={`Select ${c.id} color`}
                role="radio"
                aria-checked={selectedColorId === c.id}
              />
            ))}
          </div>

          <Separator className="bg-border/40" />

          <div
            className="grid grid-cols-6 gap-1.5 px-3 py-2.5"
            role="radiogroup"
            aria-label="Project icon"
          >
            {PROJECT_ICONS.map((i) => {
              const IconComp = i.icon;
              const isSelected = currentIcon === i.id;
              return (
                <Button
                  key={i.id}
                  type="button"
                  variant="flat"
                  size="icon-sm"
                  className={cn(
                    'size-10 rounded-md p-0 duration-150 md:size-8',
                    isSelected
                      ? 'bg-muted text-foreground ring-1 ring-foreground/20'
                      : 'hover:bg-muted/50 hover:text-foreground',
                  )}
                  onClick={() => onIconSelect(i.id)}
                  aria-label={`Select ${i.id} icon`}
                  role="radio"
                  aria-checked={isSelected}
                >
                  <IconComp className="size-4" weight="regular" />
                </Button>
              );
            })}
          </div>

          <Separator className="bg-border/40" />

          <div className="flex min-h-11 items-center justify-between gap-3 px-3">
            <Button
              type="button"
              variant="flat"
              size="sm"
              onClick={() => setOpen(false)}
              className="-ml-2 px-2 text-foreground"
            >
              Done
            </Button>
            {onReset && hasCustomIdentity && (
              <Button
                type="button"
                variant="flat"
                size="sm"
                onClick={onReset}
                className="-mr-2 px-2 text-detail font-normal hover:text-foreground"
              >
                Reset to default
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
